import * as Yup from 'yup'

import { getHeadlineShape, urlValidation } from '../../../features/validations/other'

import {
  CLICK_THROUGH_LINK,
  BUSINESS_NAME,
  HEADLINES_LIST,
  DESCRIPTIONS_LIST,
  descriptionsMaxLengthSchema,
  FILE_URL,
  headlineMaxLength,
  LANDSCAPE_IMAGE,
  NAME,
  SQUARE_IMAGE,
  SQUARE_LOGO
} from './fields'
import { CALL_TO_ACTION } from '../../ReusableFormFields/CallToActionFields/fields'
import { MEDIA_HEIGHT, MEDIA_RATIO, MEDIA_WIDTH } from '../../ReusableFormFields/AdFileUpload/fields'

export const headlineShape = getHeadlineShape(headlineMaxLength)

export const descriptionShape = Yup.object({
  text: Yup.string()
    .max(
      descriptionsMaxLengthSchema.default,
      `description should have maximum ${descriptionsMaxLengthSchema.default} characters`
    )
    .required('This field should not be empty')
})

export const listOfImagesShape = Yup.array()
  .of(
    Yup.object({
      [FILE_URL]: Yup.string().required('Please select image')
    })
  )
  .min(1, 'Please select at least one image')

export const nameValidation = Yup.string().max(400, 'Name should have maximum 400 characters').required('Name Required')

export const businessNameValidation = Yup.string()
  .max(25, 'Business name should have maximum 25 characters')
  .required('Business Name Required')

export const callToActionValidation = Yup.string().required('Please select Call To Action')

export const generalGoogleAdValidation = {
  [NAME]: nameValidation,
  [BUSINESS_NAME]: businessNameValidation,
  [CALL_TO_ACTION]: callToActionValidation,
  [CLICK_THROUGH_LINK]: urlValidation,
  [HEADLINES_LIST]: Yup.array().of(headlineShape),
  [DESCRIPTIONS_LIST]: Yup.array().of(descriptionShape),
  [`${DESCRIPTIONS_LIST}[0]['text']`]: Yup.string().test({
    name: 'Custom first description validation',
    test: (value, context) => {
      const firstDescription = context.parent[DESCRIPTIONS_LIST][0]?.text
      const firstDescriptionMaxLength = descriptionsMaxLengthSchema[0]

      if (firstDescription?.length) {
        return firstDescription?.length > firstDescriptionMaxLength
          ? context.createError({
              message: `description should have maximum ${firstDescriptionMaxLength} characters`,
              path: `${DESCRIPTIONS_LIST}[0]['text']`
            })
          : true
      }

      return context.createError({
        message: 'This field should not be empty',
        path: `${DESCRIPTIONS_LIST}[0]['text']`
      })
    }
  }),

  [LANDSCAPE_IMAGE]: listOfImagesShape,
  [SQUARE_IMAGE]: listOfImagesShape,
  [SQUARE_LOGO]: listOfImagesShape
}

const getImageAssetValidation = ({ minWidth, minHeight, allowedRatio }) => {
  return Yup.object().shape({
    [MEDIA_WIDTH]: Yup.number().min(minWidth, `The minimum supported image width is ${minWidth}px`),
    [MEDIA_HEIGHT]: Yup.number().min(minHeight, `The minimum supported image height is ${minHeight}px`),
    [MEDIA_RATIO]: Yup.number().test(MEDIA_RATIO, `Image ratio should be ${allowedRatio}:1`, ratio => {
      // rounded for 2 decimal places
      return Math.round(ratio * 100) / 100 === allowedRatio
    })
  })
}

// Landscape image (1.91:1), Min. size: 600 x 314, JPG (and jpeg), PNG, static GIF, 5120kb max size.
export const landscapeImageValidationSchema = getImageAssetValidation({
  minWidth: 600,
  minHeight: 314,
  allowedRatio: 1.91
})

// Landscape logo (4:1), Min. size: 512  x 128, JPG (and jpeg), PNG, static GIF, 5120kb max size.
export const landscapeLogoValidationSchema = getImageAssetValidation({
  minWidth: 512,
  minHeight: 128,
  allowedRatio: 4
})

// Portrait image (4:5), Min. size: 480 x 600, JPG (and jpeg), PNG, static GIF, 5120kb max size.
export const portraitImageValidationSchema = getImageAssetValidation({
  minWidth: 480,
  minHeight: 600,
  allowedRatio: 0.8 // 0.8 = 4:5
})

// Square image (1:1), Min. size: 300 x 300, JPG (and jpeg), PNG, static GIF, 5120kb max size.
export const squareImageValidationSchema = getImageAssetValidation({
  minWidth: 300,
  minHeight: 300,
  allowedRatio: 1
})

// Square logo (1:1), Min. size: 128 x 128, JPG (and jpeg), PNG, static GIF, 5120kb max size.
export const squareLogoValidationSchema = getImageAssetValidation({
  minWidth: 128,
  minHeight: 128,
  allowedRatio: 1
})
